import { useState, useEffect, lazy, Suspense } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Save, Eye, Send, Plus, Trash2, Edit, Video,
  FileText, HelpCircle, Loader2, CheckCircle, BookOpen, Layers, Settings,
  ChevronDown, ChevronRight, Youtube, Link2, Check, X, CircleDot
} from 'lucide-react';

// Lazy load MDEditor for better performance
const MDEditor = lazy(() => import('@uiw/react-md-editor').then(mod => ({ default: mod.default })));

const CATEGORIES = [
  "AI & Technology", "Public Health", "Pharmacology", "Medical Ethics",
  "Digital Health", "Research Methods", "Psychiatry", "Healthcare Management",
  "Genetics & Genomics", "Clinical Skills", "Surgery", "Pediatrics",
  "Data Science", "Academic Writing", "Emergency Medicine"
];

const LEVELS = ["Beginner", "Intermediate", "Advanced"];

const LESSON_TYPES = [
  { value: 'video', label: 'Video Lesson', icon: Video, desc: 'YouTube or Vimeo embed' },
  { value: 'text', label: 'Rich Content', icon: FileText, desc: 'Text with markdown support' },
  { value: 'quiz', label: 'Quiz / Assessment', icon: HelpCircle, desc: 'Multiple choice, true/false' },
];

// Helper to extract YouTube/Vimeo embed URL
function getVideoEmbed(url) {
  if (!url) return { embedUrl: '', provider: '' };
  // YouTube
  const ytMatch = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) {
    return { embedUrl: `https://www.youtube.com/embed/${ytMatch[1]}`, provider: 'youtube' };
  }
  // Vimeo
  const vimeoMatch = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vimeoMatch) {
    return { embedUrl: `https://player.vimeo.com/video/${vimeoMatch[1]}`, provider: 'vimeo' };
  }
  // Direct URL
  return { embedUrl: url, provider: 'direct' };
}

const WAITLIST_FILE_SIZE_LIMIT = 8 * 1024 * 1024;

const fileToDataUrl = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = () => reject(new Error('Failed to read file'));
  reader.readAsDataURL(file);
});

export default function CourseBuilder() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { user, api } = useAuth();
  const isEditMode = !!courseId;

  const [activeTab, setActiveTab] = useState('basics');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loading, setLoading] = useState(isEditMode);

  // Course basics
  const [courseData, setCourseData] = useState({
    title: '', short_description: '', description: '', category: '', level: 'Beginner',
    duration: '', instructor: '', price: '', image_url: '', certification: true, status: 'draft',
    waitlist_enabled: false
  });

  const [waitlistFile, setWaitlistFile] = useState(null);
  const [uploadingWaitlistFile, setUploadingWaitlistFile] = useState(false);

  // Modules and lessons
  const [modules, setModules] = useState([]);
  const [expandedModules, setExpandedModules] = useState({});

  // Dialogs
  const [moduleDialog, setModuleDialog] = useState({ open: false, data: null, isEdit: false });
  const [lessonDialog, setLessonDialog] = useState({ open: false, moduleId: null, data: null, isEdit: false });
  const [previewDialog, setPreviewDialog] = useState(false);

  useEffect(() => {
    if (!user?.verified) {
      toast.error('University verification required');
      navigate('/dashboard');
      return;
    }
    if (isEditMode) fetchCourse();
  }, [courseId, user]);

  const fetchCourse = async () => {
    setLoading(true);
    try {
      const [courseRes, modulesRes] = await Promise.all([
        api.get(`/courses/${courseId}`),
        api.get(`/courses/${courseId}/curriculum`)
      ]);
      const c = courseRes.data;
      console.log("Course data after fetching: ", c);
      setCourseData({
        title: c.title || '', short_description: c.short_description || '',
        description: c.description || '', category: c.category || '',
        level: c.level || 'Beginner', duration: c.duration || '',
        instructor: c.instructor || '', price: c.price?.toString() || '',
        image_url: c.image_url || '', certification: c.certification !== false,
        status: c.status || 'draft',
        waitlist_enabled: Boolean(c.waitlist_enabled)
      });
      setModules(modulesRes.data.modules || []);
      if (modulesRes.data.modules?.length > 0) {
        setExpandedModules({ [modulesRes.data.modules[0].module_id]: true });
      }
    } catch (err) {
      toast.error('Failed to load course');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (showToast = true) => {
    if (!courseData.title || !courseData.description || (!courseData.waitlist_enabled && !courseData.category)) {
      toast.error(courseData.waitlist_enabled
        ? 'Please fill in required fields (Title, Description)'
        : 'Please fill in required fields (Title, Description, Category)'
      );
      return null;
    }
    setSaving(true);
    try {
      const payload = { ...courseData, price: parseFloat(courseData.price) || 0, status: 'waitlisted' };
      let savedCourseId = courseId;
      console.log("payload before saving: ", payload)
      if (isEditMode) {
        const res = await api.put(`/university/courses/${courseId}`, payload);
        // Update local state with backend response (includes computed status)
        setCourseData(prev => ({
          ...prev,
          status: res.data.status || prev.status,
          waitlist_enabled: Boolean(res.data.waitlist_enabled)
        }));
      } else {
        const res = await api.post('/university/courses', payload);
        savedCourseId = res.data.course_id;
        setCourseData(prev => ({
          ...prev,
          status: res.data.status || prev.status,
          waitlist_enabled: Boolean(res.data.waitlist_enabled)
        }));
        navigate(`/university/course-builder/${savedCourseId}`, { replace: true });
      }

      if (waitlistFile && savedCourseId) {
        try {
          setUploadingWaitlistFile(true);
          const fileUrl = await fileToDataUrl(waitlistFile);
          await api.post(`/university/courses/${savedCourseId}/waitlist-file`, {
            filename: waitlistFile.name,
            file_url: fileUrl,
            content_type: waitlistFile.type || 'application/pdf'
          });
          setWaitlistFile(null);
          if (showToast) toast.success('Waitlist file uploaded');
        } catch (err) {
          toast.error(err.response?.data?.detail || 'Failed to upload waitlist file');
        } finally {
          setUploadingWaitlistFile(false);
        }
      }

      if (showToast) toast.success('Course saved');
      return savedCourseId;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (courseData.waitlist_enabled) {
      toast.error('Disable Waitlist to publish this course');
      return;
    }
    if (modules.length === 0) {
      toast.error('Add at least one module before publishing');
      return;
    }
    const totalLessons = modules.reduce((sum, m) => sum + (m.lessons?.length || 0), 0);
    if (totalLessons === 0) {
      toast.error('Add at least one lesson before publishing');
      return;
    }
    setPublishing(true);
    try {
      await api.put(`/university/courses/${courseId}`, { ...courseData, status: 'active' });
      setCourseData(prev => ({ ...prev, status: 'active' }));
      toast.success('Course published successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to publish');
    } finally {
      setPublishing(false);
    }
  };

  // Module operations
  const openModuleDialog = (module = null) => {
    setModuleDialog({
      open: true,
      data: module ? { title: module.title, description: module.description || '' } : { title: '', description: '' },
      isEdit: !!module,
      moduleId: module?.module_id
    });
  };

  const saveModule = async () => {
    if (!moduleDialog.data.title) { toast.error('Title required'); return; }
    try {
      if (moduleDialog.isEdit) {
        await api.put(`/university/modules/${moduleDialog.moduleId}`, moduleDialog.data);
        toast.success('Module updated');
      } else {
        let cId = courseId;
        if (!cId) {
          cId = await handleSave(false);
          if (!cId) return;
        }
        const res = await api.put(`/university/courses/${courseId}`, { ...courseData, waitlist_enabled: false });
        await api.post(`/university/courses/${cId}/modules`, {
          ...moduleDialog.data,
          order: modules.length + 1
        });
        toast.success('Module added');
      }
      setModuleDialog({ open: false, data: null, isEdit: false });
      if (courseId) fetchCourse();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save module');
    }
  };

  const deleteModule = async (moduleId) => {
    if (!window.confirm('Delete this module and all its lessons?')) return;
    try {
      const res = await api.put(`/university/courses/${courseId}`, { ...courseData, waitlist_enabled: false });
      await api.delete(`/university/modules/${moduleId}`);
      toast.success('Module deleted');
      fetchCourse();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  // Lesson operations
  const openLessonDialog = (moduleId, lesson = null) => {
    const defaultQuizData = {
      passing_score: 70,
      time_limit_minutes: null,
      questions: [],
      shuffle_questions: false,
      show_correct_answers: true
    };

    setLessonDialog({
      open: true,
      moduleId,
      data: lesson ? {
        title: lesson.title,
        type: lesson.type,
        content: lesson.content || '',
        video_url: lesson.video_url || '',
        video_provider: lesson.video_provider || '',
        duration_minutes: lesson.duration_minutes || 10,
        quiz_data: lesson.quiz_data || defaultQuizData
      } : {
        title: '',
        type: 'text',
        content: '',
        video_url: '',
        video_provider: '',
        duration_minutes: 10,
        quiz_data: defaultQuizData
      },
      isEdit: !!lesson,
      lessonId: lesson?.lesson_id
    });
  };

  const saveLesson = async () => {
    if (!lessonDialog.data.title) { toast.error('Title required'); return; }

    // Validate quiz if type is quiz
    if (lessonDialog.data.type === 'quiz') {
      if (!lessonDialog.data.quiz_data?.questions?.length) {
        toast.error('Add at least one question to your quiz');
        return;
      }
      for (const q of lessonDialog.data.quiz_data.questions) {
        if (!q.question_text?.trim()) {
          toast.error('All questions must have text');
          return;
        }
        if (q.options.length < 2) {
          toast.error('Each question needs at least 2 options');
          return;
        }
        const hasCorrect = q.options.some(opt => opt.is_correct);
        if (!hasCorrect) {
          toast.error('Each question must have at least one correct answer');
          return;
        }
      }
    }

    try {
      const module = modules.find(m => m.module_id === lessonDialog.moduleId);
      const payload = { ...lessonDialog.data };

      // Get video provider
      if (payload.type === 'video' && payload.video_url) {
        const { provider } = getVideoEmbed(payload.video_url);
        payload.video_provider = provider;
      }

      if (lessonDialog.isEdit) {
        await api.put(`/university/lessons/${lessonDialog.lessonId}`, payload);
        toast.success('Lesson updated');
      } else {
        await api.post(`/university/modules/${lessonDialog.moduleId}/lessons`, {
          ...payload,
          order: (module?.lessons?.length || 0) + 1
        });
        toast.success('Lesson added');
      }
      setLessonDialog({ open: false, moduleId: null, data: null, isEdit: false });
      const res = await api.put(`/university/courses/${courseId}`, { ...courseData, waitlist_enabled: false });
      fetchCourse();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save lesson');
    }
  };

  const deleteLesson = async (lessonId) => {
    if (!window.confirm('Delete this lesson?')) return;
    try {
      const res = await api.put(`/university/courses/${courseId}`, { ...courseData, waitlist_enabled: false });
      await api.delete(`/university/lessons/${lessonId}`);
      toast.success('Lesson deleted');
      fetchCourse();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({ ...prev, [moduleId]: !prev[moduleId] }));
  };

  const getLessonIcon = (type) => {
    const TypeIcon = LESSON_TYPES.find(t => t.value === type)?.icon || FileText;
    return <TypeIcon className="h-4 w-4" />;
  };

  // Quiz question helpers
  const addQuestion = () => {
    const newQuestion = {
      question_id: `q_${Date.now()}`,
      question_type: 'multiple_choice',
      question_text: '',
      options: [
        { text: '', is_correct: false },
        { text: '', is_correct: false }
      ],
      allow_multiple: false,
      explanation: '',
      points: 1
    };
    setLessonDialog(prev => ({
      ...prev,
      data: {
        ...prev.data,
        quiz_data: {
          ...prev.data.quiz_data,
          questions: [...(prev.data.quiz_data?.questions || []), newQuestion]
        }
      }
    }));
  };

  const updateQuestion = (qIndex, field, value) => {
    setLessonDialog(prev => {
      const questions = [...(prev.data.quiz_data?.questions || [])];
      questions[qIndex] = { ...questions[qIndex], [field]: value };
      return {
        ...prev,
        data: {
          ...prev.data,
          quiz_data: { ...prev.data.quiz_data, questions }
        }
      };
    });
  };

  const removeQuestion = (qIndex) => {
    setLessonDialog(prev => ({
      ...prev,
      data: {
        ...prev.data,
        quiz_data: {
          ...prev.data.quiz_data,
          questions: prev.data.quiz_data.questions.filter((_, i) => i !== qIndex)
        }
      }
    }));
  };

  const addOption = (qIndex) => {
    setLessonDialog(prev => {
      const questions = [...(prev.data.quiz_data?.questions || [])];
      questions[qIndex].options.push({ text: '', is_correct: false });
      return {
        ...prev,
        data: { ...prev.data, quiz_data: { ...prev.data.quiz_data, questions } }
      };
    });
  };

  const updateOption = (qIndex, oIndex, field, value) => {
    setLessonDialog(prev => {
      const questions = [...(prev.data.quiz_data?.questions || [])];
      const question = questions[qIndex];

      if (field === 'is_correct' && value && !question.allow_multiple) {
        // Single correct: uncheck others
        question.options = question.options.map((opt, i) => ({
          ...opt,
          is_correct: i === oIndex
        }));
      } else {
        question.options[oIndex] = { ...question.options[oIndex], [field]: value };
      }

      return {
        ...prev,
        data: { ...prev.data, quiz_data: { ...prev.data.quiz_data, questions } }
      };
    });
  };

  const removeOption = (qIndex, oIndex) => {
    setLessonDialog(prev => {
      const questions = [...(prev.data.quiz_data?.questions || [])];
      questions[qIndex].options = questions[qIndex].options.filter((_, i) => i !== oIndex);
      return {
        ...prev,
        data: { ...prev.data, quiz_data: { ...prev.data.quiz_data, questions } }
      };
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 text-[#0a2942] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="course-builder">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-btn">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div>
              <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                {isEditMode ? 'Edit Course' : 'Create Course'}
              </h1>
              <div className="flex items-center gap-2">
                {courseData.status === 'active' ? (
                  <Badge className="bg-emerald-100 text-emerald-700 text-xs">Published</Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-600 text-xs">Draft</Badge>
                )}
                {courseData.title && <span className="text-xs text-slate-500 truncate max-w-[200px]">{courseData.title}</span>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isEditMode && (
              <Button variant="outline" size="sm" onClick={() => setPreviewDialog(true)} data-testid="preview-btn">
                <Eye className="h-4 w-4 mr-1" /> Preview
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => handleSave()} disabled={saving} data-testid="save-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />}
              Save
            </Button>
            {isEditMode && courseData.status !== 'active' && !courseData.waitlist_enabled && (
              <Button size="sm" onClick={handlePublish} disabled={publishing} className="bg-emerald-600 hover:bg-emerald-700" data-testid="publish-btn">
                {publishing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                Publish
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex gap-1">
            {[
              { id: 'basics', label: 'Course Basics', icon: Settings },
              ...(!courseData.waitlist_enabled ? [{ id: 'curriculum', label: 'Curriculum', icon: Layers }] : []),
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                    ? 'border-[#0a2942] text-[#0a2942]'
                    : 'border-transparent text-slate-500 hover:text-slate-700'
                  }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="h-4 w-4" /> {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Basics Tab */}
        {activeTab === 'basics' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>Basic Information</h2>
                <div className="space-y-4">
                  <div className="flex items-center justify-between gap-4 p-3 rounded-lg border border-slate-200 bg-slate-50">
                    <div>
                      <Label className="text-slate-900">Enable Waitlist</Label>
                      <p className="text-xs text-slate-500 mt-0.5">Collect interest before publishing curriculum</p>
                    </div>
                    <Switch
                      checked={Boolean(courseData.waitlist_enabled)}
                      onCheckedChange={(v) => {
                        setCourseData((prev) => {
                          const next = { ...prev, waitlist_enabled: Boolean(v) };
                          return next;
                        });
                        if (v && activeTab === 'curriculum') setActiveTab('basics');
                      }}
                      id="waitlist_enabled"
                      data-testid="waitlist-toggle"
                    />
                  </div>

                  <div>
                    <Label>Course Title *</Label>
                    <Input
                      value={courseData.title}
                      onChange={e => setCourseData(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Introduction to Clinical Research"
                      className="mt-1"
                      data-testid="course-title-input"
                    />
                  </div>
                  <div>
                    <Label>Short Description</Label>
                    <Input
                      value={courseData.short_description}
                      onChange={e => setCourseData(prev => ({ ...prev, short_description: e.target.value }))}
                      placeholder="A brief tagline for your course"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Full Description *</Label>
                    <Textarea
                      value={courseData.description}
                      onChange={e => setCourseData(prev => ({ ...prev, description: e.target.value }))}
                      placeholder="Describe what students will learn..."
                      rows={5}
                      className="mt-1"
                      data-testid="course-desc-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Category {courseData.waitlist_enabled ? '' : '*'} </Label>
                      <Select value={courseData.category} onValueChange={v => setCourseData(prev => ({ ...prev, category: v }))}>
                        <SelectTrigger className="mt-1" data-testid="course-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                        <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                      </Select>
                      {courseData.waitlist_enabled && (
                        <p className="text-xs text-slate-500 mt-1">Optional while waitlist is enabled</p>
                      )}
                    </div>
                    <div>
                      <Label>Level</Label>
                      <Select value={courseData.level} onValueChange={v => setCourseData(prev => ({ ...prev, level: v }))}>
                        <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent>{LEVELS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <h2 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Manrope' }}>Additional Details</h2>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Duration</Label>
                    <Input
                      value={courseData.duration}
                      onChange={e => setCourseData(prev => ({ ...prev, duration: e.target.value }))}
                      placeholder="e.g., 8 weeks"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Instructor Name</Label>
                    <Input
                      value={courseData.instructor}
                      onChange={e => setCourseData(prev => ({ ...prev, instructor: e.target.value }))}
                      placeholder="Dr. John Smith"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Price (USD)</Label>
                    <Input
                      type="number"
                      value={courseData.price}
                      onChange={e => setCourseData(prev => ({ ...prev, price: e.target.value }))}
                      placeholder="0 for free"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Cover Image URL</Label>
                    <Input
                      value={courseData.image_url}
                      onChange={e => setCourseData(prev => ({ ...prev, image_url: e.target.value }))}
                      placeholder="https://..."
                      className="mt-1"
                    />
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-3">
                  <Switch
                    checked={courseData.certification}
                    onCheckedChange={v => setCourseData(prev => ({ ...prev, certification: v }))}
                    id="certification"
                  />
                  <Label htmlFor="certification" className="cursor-pointer">Issue certificate upon completion</Label>
                </div>

                <div className="mt-6 p-4 rounded-xl border border-slate-200 bg-slate-50">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-semibold text-slate-900">Waitlist Details PDF (optional)</p>
                      <p className="text-xs text-slate-500 mt-1">PDF only • Max 8 MB</p>
                    </div>
                    {uploadingWaitlistFile && <Loader2 className="h-4 w-4 animate-spin text-slate-600" />}
                  </div>

                  <div className="mt-3">
                    <Input
                      type="file"
                      accept="application/pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0] || null;
                        e.target.value = '';
                        if (!file) return;
                        if (file.type !== 'application/pdf') {
                          toast.error('Only PDF files are supported');
                          return;
                        }
                        if (file.size > WAITLIST_FILE_SIZE_LIMIT) {
                          toast.error('File must be 8 MB or smaller');
                          return;
                        }
                        setWaitlistFile(file);
                      }}
                      disabled={uploadingWaitlistFile}
                      data-testid="waitlist-file-input"
                    />
                    {waitlistFile && (
                      <p className="text-xs text-slate-600 mt-2 truncate">Selected: {waitlistFile.name}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              <div className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-slate-900 mb-3">Publishing Checklist</h3>
                <div className="space-y-2 text-sm">
                  {[
                    { done: !!courseData.title, label: 'Course title' },
                    { done: !!courseData.description, label: 'Description' },
                    { done: !!courseData.category || courseData.waitlist_enabled, label: courseData.waitlist_enabled ? 'Category (optional)' : 'Category selected' },
                    ...(!courseData.waitlist_enabled ? [
                      { done: modules.length > 0, label: 'At least one module' },
                      { done: modules.some(m => m.lessons?.length > 0), label: 'At least one lesson' },
                    ] : []),
                  ].map((item, i) => (
                    <div key={i} className={`flex items-center gap-2 ${item.done ? 'text-emerald-600' : 'text-slate-400'}`}>
                      <CheckCircle className="h-4 w-4" /> {item.label}
                    </div>
                  ))}
                </div>
              </div>

              {courseData.image_url && (
                <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                  <img src={courseData.image_url} alt="Cover" className="w-full h-40 object-cover" onError={e => e.target.style.display = 'none'} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Curriculum Tab */}
        {activeTab === 'curriculum' && !courseData.waitlist_enabled && (
          <div className="space-y-4">
            {!isEditMode && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
                Save your course first to start adding modules and lessons.
              </div>
            )}

            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Manrope' }}>Course Curriculum</h2>
              <Button onClick={() => openModuleDialog()} disabled={!isEditMode} data-testid="add-module-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Module
              </Button>
            </div>

            {modules.length === 0 ? (
              <div className="bg-white rounded-xl border-2 border-dashed border-slate-200 p-12 text-center">
                <Layers className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 mb-4">No modules yet. Start building your curriculum!</p>
                <Button onClick={() => openModuleDialog()} disabled={!isEditMode}>
                  <Plus className="h-4 w-4 mr-1" /> Add First Module
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {modules.map((module, idx) => (
                  <div key={module.module_id} className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid={`module-${module.module_id}`}>
                    <div
                      className="flex items-center justify-between p-4 bg-slate-50 cursor-pointer hover:bg-slate-100"
                      onClick={() => toggleModule(module.module_id)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-lg bg-[#0a2942] text-white flex items-center justify-center text-sm font-bold">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">{module.title}</p>
                          <p className="text-xs text-slate-500">{module.lessons?.length || 0} lessons</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); openModuleDialog(module); }}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); deleteModule(module.module_id); }} className="text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                        {expandedModules[module.module_id] ? <ChevronDown className="h-5 w-5 text-slate-400" /> : <ChevronRight className="h-5 w-5 text-slate-400" />}
                      </div>
                    </div>

                    {expandedModules[module.module_id] && (
                      <div className="p-4 border-t border-slate-100">
                        {module.lessons?.length === 0 ? (
                          <p className="text-sm text-slate-400 text-center py-4">No lessons in this module</p>
                        ) : (
                          <div className="space-y-2 mb-4">
                            {module.lessons?.map((lesson) => (
                              <div
                                key={lesson.lesson_id}
                                className="flex items-center justify-between p-3 bg-slate-50 rounded-lg group"
                                data-testid={`lesson-${lesson.lesson_id}`}
                              >
                                <div className="flex items-center gap-3">
                                  <div className={`h-7 w-7 rounded-full flex items-center justify-center ${lesson.type === 'quiz' ? 'bg-purple-100 text-purple-600' :
                                      lesson.type === 'video' ? 'bg-blue-100 text-blue-600' :
                                        'bg-slate-200 text-slate-600'
                                    }`}>
                                    {getLessonIcon(lesson.type)}
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-slate-700">{lesson.title}</p>
                                    <p className="text-xs text-slate-400 capitalize">
                                      {lesson.type === 'quiz' ? `Quiz • ${lesson.quiz_data?.questions?.length || 0} questions` : `${lesson.type} • ${lesson.duration_minutes} min`}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                  <Button variant="ghost" size="sm" onClick={() => openLessonDialog(module.module_id, lesson)}>
                                    <Edit className="h-3.5 w-3.5" />
                                  </Button>
                                  <Button variant="ghost" size="sm" onClick={() => deleteLesson(lesson.lesson_id)} className="text-red-600">
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </Button>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                        <Button variant="outline" size="sm" onClick={() => openLessonDialog(module.module_id)} className="w-full" data-testid={`add-lesson-${module.module_id}`}>
                          <Plus className="h-4 w-4 mr-1" /> Add Lesson
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Module Dialog */}
      <Dialog open={moduleDialog.open} onOpenChange={(open) => setModuleDialog({ ...moduleDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>{moduleDialog.isEdit ? 'Edit Module' : 'Add Module'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Module Title *</Label>
              <Input
                value={moduleDialog.data?.title || ''}
                onChange={e => setModuleDialog(prev => ({ ...prev, data: { ...prev.data, title: e.target.value } }))}
                placeholder="e.g., Introduction to Research Methods"
                className="mt-1"
                data-testid="module-title-input"
              />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea
                value={moduleDialog.data?.description || ''}
                onChange={e => setModuleDialog(prev => ({ ...prev, data: { ...prev.data, description: e.target.value } }))}
                placeholder="Brief description of this module..."
                rows={3}
                className="mt-1"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setModuleDialog({ open: false, data: null, isEdit: false })}>Cancel</Button>
            <Button onClick={saveModule} className="bg-[#0a2942] hover:bg-[#0d3a5c]" data-testid="save-module-btn">
              {moduleDialog.isEdit ? 'Update' : 'Add'} Module
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lesson Dialog */}
      <Dialog open={lessonDialog.open} onOpenChange={(open) => setLessonDialog({ ...lessonDialog, open })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>{lessonDialog.isEdit ? 'Edit Lesson' : 'Add Lesson'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-5">
            {/* Lesson Title */}
            <div>
              <Label>Lesson Title *</Label>
              <Input
                value={lessonDialog.data?.title || ''}
                onChange={e => setLessonDialog(prev => ({ ...prev, data: { ...prev.data, title: e.target.value } }))}
                placeholder="e.g., Understanding Research Ethics"
                className="mt-1"
                data-testid="lesson-title-input"
              />
            </div>

            {/* Lesson Type Selection */}
            <div>
              <Label className="mb-2 block">Lesson Type</Label>
              <div className="grid grid-cols-3 gap-3">
                {LESSON_TYPES.map(t => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setLessonDialog(prev => ({ ...prev, data: { ...prev.data, type: t.value } }))}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${lessonDialog.data?.type === t.value
                        ? 'border-[#0a2942] bg-[#0a2942]/5'
                        : 'border-slate-200 hover:border-slate-300'
                      }`}
                  >
                    <t.icon className={`h-5 w-5 mb-1 ${lessonDialog.data?.type === t.value ? 'text-[#0a2942]' : 'text-slate-400'}`} />
                    <p className="text-sm font-medium text-slate-900">{t.label}</p>
                    <p className="text-xs text-slate-500">{t.desc}</p>
                  </button>
                ))}
              </div>
            </div>

            {/* Video URL (for video type) */}
            {lessonDialog.data?.type === 'video' && (
              <div className="space-y-3">
                <div>
                  <Label>Video URL (YouTube or Vimeo)</Label>
                  <div className="mt-1 relative">
                    <Input
                      value={lessonDialog.data?.video_url || ''}
                      onChange={e => setLessonDialog(prev => ({ ...prev, data: { ...prev.data, video_url: e.target.value } }))}
                      placeholder="https://www.youtube.com/watch?v=..."
                      className="pl-10"
                    />
                    <Youtube className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Paste a YouTube or Vimeo link. Direct uploads coming soon.</p>
                </div>

                {lessonDialog.data?.video_url && (
                  <div className="bg-slate-100 rounded-lg aspect-video overflow-hidden">
                    <iframe
                      src={getVideoEmbed(lessonDialog.data.video_url).embedUrl}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      title="Video preview"
                    />
                  </div>
                )}

                <div>
                  <Label>Duration (minutes)</Label>
                  <Input
                    type="number"
                    value={lessonDialog.data?.duration_minutes || 10}
                    onChange={e => setLessonDialog(prev => ({ ...prev, data: { ...prev.data, duration_minutes: parseInt(e.target.value) || 10 } }))}
                    className="mt-1 w-32"
                  />
                </div>
              </div>
            )}

            {/* Rich Text Content (for text type) */}
            {lessonDialog.data?.type === 'text' && (
              <div>
                <Label>Content * (Supports Markdown)</Label>
                <div className="mt-1 border rounded-lg overflow-hidden" data-color-mode="light">
                  <Suspense fallback={<div className="h-64 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin" /></div>}>
                    <MDEditor
                      value={lessonDialog.data?.content || ''}
                      onChange={content => setLessonDialog(prev => ({ ...prev, data: { ...prev.data, content: content || '' } }))}
                      height={300}
                      preview="edit"
                    />
                  </Suspense>
                </div>
                <p className="text-xs text-slate-500 mt-1">Use markdown syntax: **bold**, *italic*, # Heading, - List, etc.</p>
                <div className="mt-3">
                  <Label>Duration (minutes)</Label>
                  <Input
                    type="number"
                    value={lessonDialog.data?.duration_minutes || 10}
                    onChange={e => setLessonDialog(prev => ({ ...prev, data: { ...prev.data, duration_minutes: parseInt(e.target.value) || 10 } }))}
                    className="mt-1 w-32"
                  />
                </div>
              </div>
            )}

            {/* Quiz Builder (for quiz type) */}
            {lessonDialog.data?.type === 'quiz' && (
              <div className="space-y-4">
                {/* Quiz Settings */}
                <div className="bg-slate-50 rounded-lg p-4 space-y-4">
                  <h4 className="font-medium text-slate-900">Quiz Settings</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Passing Score (%)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={lessonDialog.data?.quiz_data?.passing_score || 70}
                        onChange={e => setLessonDialog(prev => ({
                          ...prev,
                          data: {
                            ...prev.data,
                            quiz_data: { ...prev.data.quiz_data, passing_score: parseInt(e.target.value) || 70 }
                          }
                        }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Time Limit (minutes, optional)</Label>
                      <Input
                        type="number"
                        value={lessonDialog.data?.quiz_data?.time_limit_minutes || ''}
                        onChange={e => setLessonDialog(prev => ({
                          ...prev,
                          data: {
                            ...prev.data,
                            quiz_data: { ...prev.data.quiz_data, time_limit_minutes: e.target.value ? parseInt(e.target.value) : null }
                          }
                        }))}
                        placeholder="No limit"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      checked={lessonDialog.data?.quiz_data?.show_correct_answers !== false}
                      onCheckedChange={v => setLessonDialog(prev => ({
                        ...prev,
                        data: {
                          ...prev.data,
                          quiz_data: { ...prev.data.quiz_data, show_correct_answers: v }
                        }
                      }))}
                      id="show-answers"
                    />
                    <Label htmlFor="show-answers" className="cursor-pointer text-sm">Show correct answers after submission</Label>
                  </div>
                </div>

                {/* Questions */}
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-medium text-slate-900">Questions ({lessonDialog.data?.quiz_data?.questions?.length || 0})</h4>
                    <Button type="button" size="sm" onClick={addQuestion} data-testid="add-question-btn">
                      <Plus className="h-4 w-4 mr-1" /> Add Question
                    </Button>
                  </div>

                  {lessonDialog.data?.quiz_data?.questions?.map((question, qIndex) => (
                    <div key={question.question_id} className="border border-slate-200 rounded-lg p-4 space-y-4" data-testid={`question-${qIndex}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 space-y-3">
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="shrink-0">Q{qIndex + 1}</Badge>
                            <Select
                              value={question.question_type}
                              onValueChange={v => updateQuestion(qIndex, 'question_type', v)}
                            >
                              <SelectTrigger className="w-48">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="multiple_choice">Multiple Choice</SelectItem>
                                <SelectItem value="true_false">True / False</SelectItem>
                              </SelectContent>
                            </Select>
                            {question.question_type === 'multiple_choice' && (
                              <div className="flex items-center gap-2">
                                <Switch
                                  checked={question.allow_multiple}
                                  onCheckedChange={v => updateQuestion(qIndex, 'allow_multiple', v)}
                                  id={`allow-multiple-${qIndex}`}
                                />
                                <Label htmlFor={`allow-multiple-${qIndex}`} className="text-xs cursor-pointer">Multiple correct</Label>
                              </div>
                            )}
                          </div>

                          <Input
                            value={question.question_text}
                            onChange={e => updateQuestion(qIndex, 'question_text', e.target.value)}
                            placeholder="Enter your question..."
                            className="font-medium"
                          />
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeQuestion(qIndex)}
                          className="text-red-600 shrink-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>

                      {/* Options */}
                      <div className="space-y-2 pl-4">
                        <Label className="text-xs text-slate-500">
                          {question.allow_multiple ? 'Check all correct answers:' : 'Select the correct answer:'}
                        </Label>

                        {question.question_type === 'true_false' ? (
                          <div className="space-y-2">
                            {[{ text: 'True', idx: 0 }, { text: 'False', idx: 1 }].map(({ text, idx }) => {
                              const isCorrect = question.options[idx]?.is_correct;
                              return (
                                <div
                                  key={text}
                                  className={`flex items-center gap-3 p-2 rounded-lg border cursor-pointer transition-colors ${isCorrect ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300'
                                    }`}
                                  onClick={() => {
                                    const newOptions = [
                                      { text: 'True', is_correct: idx === 0 },
                                      { text: 'False', is_correct: idx === 1 }
                                    ];
                                    updateQuestion(qIndex, 'options', newOptions);
                                  }}
                                >
                                  <CircleDot className={`h-4 w-4 ${isCorrect ? 'text-emerald-600' : 'text-slate-300'}`} />
                                  <span className={isCorrect ? 'font-medium text-emerald-700' : 'text-slate-700'}>{text}</span>
                                  {isCorrect && <Check className="h-4 w-4 text-emerald-600 ml-auto" />}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <>
                            {question.options.map((option, oIndex) => (
                              <div key={oIndex} className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => updateOption(qIndex, oIndex, 'is_correct', !option.is_correct)}
                                  className={`h-6 w-6 rounded-full border-2 flex items-center justify-center shrink-0 transition-colors ${option.is_correct
                                      ? 'border-emerald-500 bg-emerald-500 text-white'
                                      : 'border-slate-300 hover:border-slate-400'
                                    }`}
                                >
                                  {option.is_correct && <Check className="h-3.5 w-3.5" />}
                                </button>
                                <Input
                                  value={option.text}
                                  onChange={e => updateOption(qIndex, oIndex, 'text', e.target.value)}
                                  placeholder={`Option ${oIndex + 1}`}
                                  className={option.is_correct ? 'border-emerald-300 bg-emerald-50' : ''}
                                />
                                {question.options.length > 2 && (
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => removeOption(qIndex, oIndex)}
                                    className="text-slate-400 hover:text-red-600"
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                )}
                              </div>
                            ))}

                            {question.options.length < 6 && (
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => addOption(qIndex)}
                                className="text-slate-500"
                              >
                                <Plus className="h-4 w-4 mr-1" /> Add Option
                              </Button>
                            )}
                          </>
                        )}
                      </div>

                      {/* Explanation */}
                      <div className="pl-4">
                        <Label className="text-xs text-slate-500">Explanation (shown after answer)</Label>
                        <Textarea
                          value={question.explanation || ''}
                          onChange={e => updateQuestion(qIndex, 'explanation', e.target.value)}
                          placeholder="Explain why this is the correct answer..."
                          rows={2}
                          className="mt-1"
                        />
                      </div>
                    </div>
                  ))}

                  {(!lessonDialog.data?.quiz_data?.questions || lessonDialog.data.quiz_data.questions.length === 0) && (
                    <div className="border-2 border-dashed border-slate-200 rounded-lg p-8 text-center">
                      <HelpCircle className="h-10 w-10 text-slate-300 mx-auto mb-2" />
                      <p className="text-slate-500 mb-3">No questions yet</p>
                      <Button type="button" onClick={addQuestion}>
                        <Plus className="h-4 w-4 mr-1" /> Add First Question
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLessonDialog({ open: false, moduleId: null, data: null, isEdit: false })}>Cancel</Button>
            <Button onClick={saveLesson} className="bg-[#0a2942] hover:bg-[#0d3a5c]" data-testid="save-lesson-btn">
              {lessonDialog.isEdit ? 'Update' : 'Add'} Lesson
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Manrope' }}>Course Preview</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-6">
              <Badge className="mb-2">{courseData.category || 'No category'}</Badge>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">{courseData.title || 'Untitled Course'}</h2>
              <p className="text-slate-600 mb-4">{courseData.description || 'No description'}</p>
              <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                <span>{courseData.duration || 'Duration TBD'}</span>
                <span>{courseData.instructor || 'Instructor TBD'}</span>
                <span>{courseData.level}</span>
                <span>{parseFloat(courseData.price) > 0 ? `$${courseData.price}` : 'Free'}</span>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 mb-3">Curriculum ({modules.length} modules)</h3>
              {modules.map((m, idx) => (
                <div key={m.module_id} className="border-b border-slate-100 py-3">
                  <p className="font-medium">Module {idx + 1}: {m.title}</p>
                  <p className="text-sm text-slate-500">{m.lessons?.length || 0} lessons</p>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPreviewDialog(false)}>Close</Button>
            <Button onClick={() => window.open(`/courses/${courseId}`, '_blank')} className="bg-[#0a2942]">
              <Eye className="h-4 w-4 mr-1" /> View Public Page
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
